import { z } from 'zod';
import { IsoDate, IsoDateTime, PositiveDecimalString, UuidString } from '../../primitives.js';

/**
 * Worklog — учёт времени по задаче (BE-S13-1).
 *
 * COGS-loop: для задач с subject_type ∈ {work_order, job_card} backend
 * денормализует work_order_id/job_card_id + labor_rate snapshot в момент
 * записи — cost-engine читает labor cost из worklogs без join к users
 * (rate меняется со временем, snapshot фиксирует фактическую ставку).
 */

/** B19 — рукой или таймером. Enum, а не пометка в `note`: константа в данных непереводима. */
export const WorklogSource = z.enum(['manual', 'timer']);
export type WorklogSource = z.infer<typeof WorklogSource>;

export const Worklog = z.object({
  id: UuidString,
  entity_id: UuidString,
  task_id: UuidString,
  user_id: UuidString,
  /** Decimal-string, часы. */
  duration_hours: PositiveDecimalString,
  work_date: IsoDate,
  /** Структурный комментарий не нужен — описание работ через note (plain). */
  note: z.string().max(1000).nullable(),
  /** COGS denormalization (только для WO/JobCard задач). */
  work_order_id: UuidString.nullable(),
  job_card_id: UuidString.nullable(),
  /** Snapshot ставки в момент записи (Decimal-string, валюта cost_currency). */
  labor_rate: PositiveDecimalString.nullable(),
  cost_currency: z.string().length(3).nullable(),
  /** BE-2 — кто последним изменил запись (для «изменено {кем}» в UI). */
  updated_by_user_id: UuidString.nullable().optional(),
  /**
   * B19 (§3.8) — откуда взялась запись. Без различения забытый на ночь таймер
   * неотличим от осознанно введённых девяти часов.
   *
   * Nullable-**optional**: вкладка, открытая до выката поля, не падает (§6.2).
   */
  source: WorklogSource.optional(),
  /**
   * B22 (§3.8) — след правки ИМЕННО часов: кто и когда. Ставится только при
   * фактической смене `duration_hours`, поэтому отличается от
   * `updated_by_user_id`, который меняет любой PATCH (включая правку примечания).
   */
  edited_by_user_id: UuidString.nullable().optional(),
  edited_at: IsoDateTime.nullable().optional(),
  /**
   * WTU-F10 — часы ДО последней правки. Без них след говорил «время изменено»,
   * не говоря, с чего: прежнее значение жило только в
   * `audit_events.request_payload`, то есть было доступно расследованию, а не
   * человеку, читающему журнал задачи.
   *
   * Nullable-**optional** по той же причине, что `source`: записи, правленные
   * до этой миграции, прежнего значения не знают, и вкладка не должна падать.
   */
  edited_hours_before: PositiveDecimalString.nullable().optional(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type Worklog = z.infer<typeof Worklog>;

/**
 * B24 пака `tails-closeout` (WL-B1) — номер производственного заказа для строки
 * ворклога: без него журнал показывал «Заказ 3f2a…» (UUID) вместо «WO-2026-00012».
 * Ключ — `work_order_id` строки; один select на весь список, не по строке.
 */
export const WorkOrderDirectory = z.record(
  UuidString,
  z.object({ voucher_no: z.string() }).passthrough(),
);
export type WorkOrderDirectory = z.infer<typeof WorkOrderDirectory>;
